import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from "react"

import type {
  DispatchBoardReadV1,
  DispatchBoardRouteV1,
  DispatchBoardStopV1,
  DispatchOptimizationJobV1,
  DispatchRouteDraftV1,
  MapCoordinateV1,
  PropertyMapConfigurationV1,
} from "@impetusos/contracts"

import {
  ConsoleRequestError,
  dispatchBoardAction,
  dispatchDayOfChange,
  propertyMapImageUrl,
  readDispatchBoard,
  readDispatchOptimizationJob,
  readPropertyMapConfiguration,
  startDispatchOptimization,
} from "../transport/index.js"

type BoardStatus = "loading" | "ready" | "denied" | "retry"
type Notice = { tone: "success" | "warning" | "danger"; message: string }
type DayOfSelection = {
  routeId: DispatchBoardRouteV1["routeId"]
  stop: DispatchBoardStopV1
}
type DispatchBoardState = {
  status: BoardStatus
  board: DispatchBoardReadV1 | null
  map: PropertyMapConfigurationV1 | null
  routes: DispatchRouteDraftV1[]
  dirty: boolean
  reason: string
  busy: string | null
  notice: Notice | null
  jobId: string | null
  job: DispatchOptimizationJobV1 | null
  publishOpen: boolean
  dayOfSelection: DayOfSelection | null
  dayOfAction: "arrival_window" | "cancel"
  dayOfMinutes: number
}

const INITIAL_STATE: DispatchBoardState = {
  status: "loading",
  board: null,
  map: null,
  routes: [],
  dirty: false,
  reason: "Dispatcher reviewed the current Branch plan.",
  busy: null,
  notice: null,
  jobId: null,
  job: null,
  publishOpen: false,
  dayOfSelection: null,
  dayOfAction: "arrival_window",
  dayOfMinutes: 15,
}

const mergeDispatchBoardState = (
  current: DispatchBoardState,
  update: Partial<DispatchBoardState>
): DispatchBoardState => ({ ...current, ...update })

const EXPLANATIONS: Readonly<Record<string, string>> = {
  travel_window_conflict: "Travel time exceeds the available gap between Visits.",
  route_unreachable: "The route provider could not find a drivable path.",
  visit_stale: "A Visit changed after this draft was prepared.",
  unassigned: "This Visit does not have a Worker or Crew.",
  not_in_dispatch_run: "This assigned Visit is not included in the current run.",
  worker_relationship_inactive: "The Worker's relationship is not active for this work.",
  worker_branch_assignment_missing: "The Worker is not assigned to this Branch.",
  worker_skill_missing: "The Worker is missing a required skill.",
  worker_capacity_exceeded: "The assignment exceeds the Worker's capacity.",
  resource_unavailable: "A required vehicle or operational resource is unavailable.",
  availability_conflict: "The Visit falls outside the resource's availability.",
  operational_hold: "An operational hold blocks dispatch.",
}

const TIME_FORMATTERS = new Map<string, Intl.DateTimeFormat>()
const DATE_FORMATTER = new Intl.DateTimeFormat("en-US", {
  weekday: "long",
  month: "long",
  day: "numeric",
  year: "numeric",
  timeZone: "UTC",
})

const humanize = (code: string): string =>
  EXPLANATIONS[code]
  ?? code.replaceAll("_", " ").replace(/^\w/, (value) => value.toUpperCase())

const draftRoutes = (
  routes: readonly DispatchBoardRouteV1[]
): DispatchRouteDraftV1[] => routes.map((route) => ({
  routeId: route.routeId,
  assignedWorkerId: route.assignedWorkerId,
  assignedCrewId: route.assignedCrewId,
  stops: [...route.stops]
    .sort((left, right) => left.sequence - right.sequence)
    .map((stop) => ({
      stopId: stop.stopId,
      visitId: stop.visitId,
      sequence: stop.sequence,
    })),
}))

const timeLabel = (iso: string, timezone: string): string =>
  (() => {
    let formatter = TIME_FORMATTERS.get(timezone)
    if (formatter === undefined) {
      formatter = new Intl.DateTimeFormat("en-US", {
        hour: "numeric",
        minute: "2-digit",
        timeZone: timezone,
      })
      TIME_FORMATTERS.set(timezone, formatter)
    }
    return formatter.format(new Date(iso))
  })()

const durationLabel = (seconds: number): string => {
  if (seconds < 60) return `${seconds} sec`
  const minutes = Math.round(seconds / 60)
  return minutes < 60
    ? `${minutes} min`
    : `${Math.floor(minutes / 60)} hr ${minutes % 60} min`
}

const distanceLabel = (meters: number): string =>
  `${(meters / 1609.344).toFixed(meters < 16_094 ? 1 : 0)} mi`

const dateLabel = (value: string): string =>
  DATE_FORMATTER.format(new Date(`${value}T12:00:00Z`))

const Progress = ({ job }: { job: DispatchOptimizationJobV1 }) => (
  <section className="dispatch-progress" aria-live="polite" aria-label="Route planning progress">
    <div className="dispatch-progress-copy">
      <span className={`dispatch-job-dot ${job.status}`} aria-hidden="true" />
      <div>
        <strong>{job.message}</strong>
        <span>{job.progressPercent}% complete</span>
      </div>
    </div>
    <div
      className="dispatch-progress-track"
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={job.progressPercent}
    >
      <span style={{ transform: `scaleX(${job.progressPercent / 100})` }} />
    </div>
  </section>
)

const RouteMap = ({
  board,
  configuration,
}: {
  board: DispatchBoardReadV1
  configuration: PropertyMapConfigurationV1 | null
}) => {
  const points = board.routes.flatMap((route) =>
    route.stops.flatMap((stop) => stop.coordinate === null ? [] : [{
      routeId: route.routeId,
      sequence: stop.sequence,
      label: stop.label,
      coordinate: stop.coordinate,
    }])
  )
  if (points.length === 0 || configuration === null) {
    return (
      <div className="dispatch-map-empty">
        <span aria-hidden="true">+</span>
        <strong>Map coordinates unavailable</strong>
        <p>Route planning stays blocked until every Service Location has a coordinate.</p>
      </div>
    )
  }
  const longitudes = points.map((point) => point.coordinate.longitude)
  const latitudes = points.map((point) => point.coordinate.latitude)
  const minimumLongitude = Math.min(...longitudes)
  const maximumLongitude = Math.max(...longitudes)
  const minimumLatitude = Math.min(...latitudes)
  const maximumLatitude = Math.max(...latitudes)
  const longitudeSpan = Math.max(maximumLongitude - minimumLongitude, 0.01)
  const latitudeSpan = Math.max(maximumLatitude - minimumLatitude, 0.01)
  const center: MapCoordinateV1 = {
    longitude: (minimumLongitude + maximumLongitude) / 2,
    latitude: (minimumLatitude + maximumLatitude) / 2,
  }
  const marker = (coordinate: MapCoordinateV1) => ({
    x: 12 + (coordinate.longitude - minimumLongitude) / longitudeSpan * 76,
    y: 12 + (maximumLatitude - coordinate.latitude) / latitudeSpan * 76,
  })
  const routeColors = new Map(
    board.routes.map((route, index) => [
      route.routeId,
      ["#1e6656", "#c06b3e", "#385d8a", "#7a5c9a"][index % 4]!,
    ])
  )
  return (
    <div className="dispatch-map">
      <img
        src={propertyMapImageUrl(configuration, center, 12)}
        alt="Satellite overview of the current Dispatch Run"
      />
      <svg
        className="dispatch-map-routes"
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        {board.routes.map((route) => {
          const routePoints = route.stops.flatMap((stop) =>
            stop.coordinate === null ? [] : [marker(stop.coordinate)]
          )
          return routePoints.length > 1
            ? (
                <polyline
                  key={route.routeId}
                  points={routePoints.map((point) => `${point.x},${point.y}`).join(" ")}
                  fill="none"
                  stroke={routeColors.get(route.routeId)}
                  strokeWidth="0.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  vectorEffect="non-scaling-stroke"
                />
              )
            : null
        })}
      </svg>
      {points.map((point) => {
        const position = marker(point.coordinate)
        return (
          <span
            key={`${point.routeId}-${point.sequence}`}
            className="dispatch-map-marker"
            style={{
              left: `${position.x}%`,
              top: `${position.y}%`,
              background: routeColors.get(point.routeId),
            }}
            title={`${point.sequence}. ${point.label}`}
          >
            {point.sequence}
          </span>
        )
      })}
      <div className="dispatch-map-caption">
        <span>{points.length} mapped Visits</span>
        <span>Mapbox / OpenStreetMap</span>
      </div>
    </div>
  )
}

const ProposalPanel = ({
  board,
  busy,
  onApply,
}: {
  board: DispatchBoardReadV1
  busy: boolean
  onApply: () => void
}) => {
  const proposal = board.proposal
  if (proposal === null) return null
  const applied = board.planning.proposalId === proposal.entityId
  return (
    <section
      className={`dispatch-proposal ${proposal.state.toLowerCase()}`}
      aria-labelledby="proposal-title"
    >
      <div>
        <p className="eyebrow">Latest route proposal</p>
        <h3 id="proposal-title">
          {proposal.state === "Feasible" ? "Ready for dispatcher review" : "Conflicts need attention"}
        </h3>
        <p>
          {proposal.state === "Feasible"
            ? "Every current Visit, resource, travel, and time-window check passed."
            : "The draft was not changed. Resolve or override the explained constraints, then run planning again."}
        </p>
      </div>
      {proposal.blockerCodes.length > 0 && (
        <ul className="dispatch-conflicts">
          {proposal.blockerCodes.map((code) => (
            <li key={code}><span aria-hidden="true">!</span><div><strong>{humanize(code)}</strong><code>{code}</code></div></li>
          ))}
        </ul>
      )}
      <div className="dispatch-proposal-meta">
        {proposal.routeExplanations.map((route) => (
          <span key={route.routeId}>
            {durationLabel(route.totalTravelSeconds)} travel
            <i aria-hidden="true" />
            {distanceLabel(route.totalDistanceMeters)}
          </span>
        ))}
      </div>
      {proposal.state === "Feasible" && !applied && (
        <button
          type="button"
          className="dispatch-button primary"
          disabled={busy}
          onClick={onApply}
        >
          Apply this proposal
        </button>
      )}
      {applied && <span className="dispatch-applied">Applied to draft</span>}
    </section>
  )
}

const DispatchLane = ({
  detail,
  route,
  routeIndex,
  boardState,
  canDayOf,
  busy,
  onLock,
  onMove,
  onPin,
  onDayOf,
}: {
  detail: DispatchBoardRouteV1
  route: DispatchRouteDraftV1
  routeIndex: number
  boardState: DispatchBoardReadV1["state"]
  canDayOf: boolean
  busy: boolean
  onLock: (routeId: DispatchBoardRouteV1["routeId"], locked: boolean) => void
  onMove: (routeIndex: number, stopIndex: number, direction: -1 | 1) => void
  onPin: (
    stopId: DispatchBoardRouteV1["stops"][number]["stopId"],
    pinned: boolean
  ) => void
  onDayOf: (
    routeId: DispatchBoardRouteV1["routeId"],
    stop: DispatchBoardStopV1,
    trigger: HTMLButtonElement
  ) => void
}) => (
  <article className="dispatch-lane">
    <header>
      <div className="dispatch-resource-avatar" aria-hidden="true">
        {detail.resourceLabel.split(" ").map((part) => part[0]).join("").slice(0, 2)}
      </div>
      <div>
        <h4>{detail.resourceLabel}</h4>
        <p>
          {detail.stops.length} Visits
          <i aria-hidden="true" />
          {durationLabel(detail.scheduledMinutes * 60)} work
          <i aria-hidden="true" />
          {durationLabel(detail.totalTravelSeconds)} travel
        </p>
      </div>
      <button
        type="button"
        className={`dispatch-lock ${detail.locked ? "active" : ""}`}
        aria-pressed={detail.locked}
        disabled={busy || boardState !== "Draft"}
        onClick={() => onLock(detail.routeId, !detail.locked)}
      >
        {detail.locked ? "Locked" : "Lock route"}
      </button>
    </header>
    {detail.blockerCodes.length > 0 && (
      <div className="dispatch-route-conflict" role="alert">
        <strong>Route conflict</strong>
        <ul>{detail.blockerCodes.map((code) => <li key={code}>{humanize(code)}</li>)}</ul>
      </div>
    )}
    <ol className="dispatch-stop-list">
      {route.stops.map((stop, stopIndex) => {
        const original = detail.stops.find((item) => item.stopId === stop.stopId)
        if (original === undefined) return null
        return (
          <li key={stop.stopId} className={original.pinned ? "pinned" : ""}>
            <span className="dispatch-sequence">{stopIndex + 1}</span>
            <span className="dispatch-stop-line" aria-hidden="true" />
            <div className="dispatch-stop-main">
              <div className="dispatch-stop-time">
                <strong>{timeLabel(original.startsAt, original.timezone)}</strong>
                <span>to {timeLabel(original.endsAt, original.timezone)}</span>
              </div>
              <div className="dispatch-stop-address">
                <strong>{original.label}</strong>
                <span>{original.address}</span>
                <small>Visit {original.visitId.slice(0, 8)} / v{original.visitVersion}</small>
              </div>
            </div>
            <div className="dispatch-stop-actions" aria-label={`Dispatch controls for ${original.label}`}>
              <button
                type="button"
                disabled={
                  boardState !== "Draft"
                  || detail.locked
                  || original.pinned
                  || stopIndex === 0
                }
                onClick={() => onMove(routeIndex, stopIndex, -1)}
              >
                Move earlier
              </button>
              <button
                type="button"
                disabled={
                  boardState !== "Draft"
                  || detail.locked
                  || original.pinned
                  || stopIndex === route.stops.length - 1
                }
                onClick={() => onMove(routeIndex, stopIndex, 1)}
              >
                Move later
              </button>
              <button
                type="button"
                className={original.pinned ? "active" : ""}
                aria-pressed={original.pinned}
                disabled={busy || boardState !== "Draft"}
                onClick={() => onPin(original.stopId, !original.pinned)}
              >
                {original.pinned ? "Pinned" : "Pin Visit"}
              </button>
              {boardState === "Published" && canDayOf && (
                <button
                  type="button"
                  className="day-of"
                  disabled={busy}
                  onClick={(event) => onDayOf(detail.routeId, original, event.currentTarget)}
                >
                  Day-of change
                </button>
              )}
            </div>
          </li>
        )
      })}
    </ol>
    <footer>
      <span>{distanceLabel(detail.totalDistanceMeters)} planned travel</span>
      <code title={detail.routeId}>{detail.routeId.slice(0, 8)}</code>
    </footer>
  </article>
)

const DayOfDialog = ({
  open,
  dialogRef,
  confirmRef,
  selection,
  action,
  minutes,
  reason,
  busy,
  onAction,
  onMinutes,
  onCancel,
  onConfirm,
}: {
  open: boolean
  dialogRef: React.RefObject<HTMLDialogElement | null>
  confirmRef: React.RefObject<HTMLButtonElement | null>
  selection: DayOfSelection | null
  action: "arrival_window" | "cancel"
  minutes: number
  reason: string
  busy: string | null
  onAction: (value: "arrival_window" | "cancel") => void
  onMinutes: (value: number) => void
  onCancel: () => void
  onConfirm: () => void
}) => {
  if (!open || selection === null) return null
  const nextStart = new Date(
    Date.parse(selection.stop.startsAt) + minutes * 60_000
  ).toISOString()
  const nextEnd = new Date(
    Date.parse(selection.stop.endsAt) + minutes * 60_000
  ).toISOString()
  return (
    <dialog
      ref={dialogRef}
      className="dispatch-dialog day-of-dialog"
      aria-labelledby="dispatch-day-of-title"
      aria-describedby="dispatch-day-of-description"
      onCancel={(event) => {
        if (busy !== null) {
          event.preventDefault()
          return
        }
        onCancel()
      }}
    >
      <div className="dispatch-dialog-mark" aria-hidden="true">D</div>
      <p className="eyebrow">Published route recovery</p>
      <h3 id="dispatch-day-of-title">Record a day-of change?</h3>
      <p id="dispatch-day-of-description">
        The current publication stays immutable. Confirmation creates and immediately
        publishes the next version, preserves the before/after history, and queues
        deduplicated notices for affected users.
      </p>
      <div className="dispatch-day-of-fields">
        <label>
          <span>Change</span>
          <select
            value={action}
            disabled={busy !== null}
            onChange={(event) => onAction(
              event.target.value === "cancel" ? "cancel" : "arrival_window"
            )}
          >
            <option value="arrival_window">Adjust arrival window</option>
            <option value="cancel">Cancel this Visit</option>
          </select>
        </label>
        {action === "arrival_window" && (
          <label>
            <span>Move window</span>
            <select
              value={minutes}
              disabled={busy !== null}
              onChange={(event) => onMinutes(Number(event.target.value))}
            >
              <option value={-30}>30 minutes earlier</option>
              <option value={-15}>15 minutes earlier</option>
              <option value={15}>15 minutes later</option>
              <option value={30}>30 minutes later</option>
              <option value={60}>1 hour later</option>
            </select>
          </label>
        )}
      </div>
      <dl>
        <div><dt>Visit</dt><dd>{selection.stop.label}</dd></div>
        <div>
          <dt>Current</dt>
          <dd>
            {timeLabel(selection.stop.startsAt, selection.stop.timezone)}
            {" – "}{timeLabel(selection.stop.endsAt, selection.stop.timezone)}
          </dd>
        </div>
        {action === "arrival_window" && (
          <div>
            <dt>New window</dt>
            <dd>
              {timeLabel(nextStart, selection.stop.timezone)}
              {" – "}{timeLabel(nextEnd, selection.stop.timezone)}
            </dd>
          </div>
        )}
        <div><dt>Reason</dt><dd>{reason}</dd></div>
      </dl>
      {action === "cancel" && (
        <p className="dispatch-day-of-warning" role="alert">
          Cancellation removes this Visit from the active published route. This cannot
          be undone by editing the prior publication.
        </p>
      )}
      <div className="dispatch-dialog-actions">
        <button
          type="button"
          className="dispatch-button secondary"
          disabled={busy !== null}
          onClick={onCancel}
        >
          Keep current route
        </button>
        <button
          ref={confirmRef}
          type="button"
          className={`dispatch-button ${action === "cancel" ? "publish" : "primary"}`}
          disabled={busy !== null || reason.trim().length < 2}
          onClick={onConfirm}
        >
          {busy === "day-of"
            ? "Publishing change..."
            : action === "cancel"
              ? "Cancel Visit and publish"
              : "Adjust window and publish"}
        </button>
      </div>
    </dialog>
  )
}

const PublishDialog = ({
  open,
  dialogRef,
  confirmRef,
  board,
  reason,
  busy,
  onCancel,
  onPublish,
}: {
  open: boolean
  dialogRef: React.RefObject<HTMLDialogElement | null>
  confirmRef: React.RefObject<HTMLButtonElement | null>
  board: DispatchBoardReadV1
  reason: string
  busy: string | null
  onCancel: () => void
  onPublish: () => void
}) => {
  if (!open) return null
  const visitCount = board.routes.reduce((sum, route) => sum + route.stops.length, 0)
  return (
    <dialog
      ref={dialogRef}
      className="dispatch-dialog"
      aria-labelledby="dispatch-publish-title"
      aria-describedby="dispatch-publish-description"
      onCancel={(event) => {
        if (busy !== null) {
          event.preventDefault()
          return
        }
        onCancel()
      }}
    >
      <div className="dispatch-dialog-mark" aria-hidden="true">P</div>
      <p className="eyebrow">Immutable dispatch version</p>
      <h3 id="dispatch-publish-title">Publish this Branch plan?</h3>
      <p id="dispatch-publish-description">
        This will create published version {board.latestPublishedVersion + 1} for
        {" "}{board.routes.length} routes and {visitCount} Visits. Future edits will
        open a new draft and cannot change this version.
      </p>
      <dl>
        <div><dt>Feasibility</dt><dd>{board.planning.feasibility}</dd></div>
        <div><dt>Changes</dt><dd>{board.diff.movedStopCount} moved Visits</dd></div>
        <div><dt>Reason</dt><dd>{reason}</dd></div>
      </dl>
      <div className="dispatch-dialog-actions">
        <button
          type="button"
          className="dispatch-button secondary"
          disabled={busy !== null}
          onClick={onCancel}
        >
          Keep reviewing
        </button>
        <button
          ref={confirmRef}
          type="button"
          className="dispatch-button publish"
          disabled={busy !== null}
          onClick={onPublish}
        >
          {busy === "publish" ? "Publishing..." : "Publish immutable version"}
        </button>
      </div>
    </dialog>
  )
}

const useDispatchBoardController = () => {
  const [state, updateState] = useReducer(mergeDispatchBoardState, INITIAL_STATE)
  const {
    status,
    board,
    map,
    routes,
    dirty,
    reason,
    busy,
    notice,
    jobId,
    job,
    publishOpen,
    dayOfSelection,
    dayOfAction,
    dayOfMinutes,
  } = state
  const publishButton = useRef<HTMLButtonElement>(null)
  const confirmButton = useRef<HTMLButtonElement>(null)
  const publishDialog = useRef<HTMLDialogElement>(null)
  const dayOfDialog = useRef<HTMLDialogElement>(null)
  const dayOfConfirmButton = useRef<HTMLButtonElement>(null)
  const dayOfReturnFocus = useRef<HTMLButtonElement | null>(null)

  const load = useCallback(async () => {
    updateState({ status: "loading" })
    try {
      const [nextBoard, nextMap] = await Promise.all([
        readDispatchBoard(),
        readPropertyMapConfiguration().catch(() => null),
      ])
      updateState({
        board: nextBoard,
        map: nextMap,
        routes: draftRoutes(nextBoard.routes),
        dirty: false,
        status: "ready",
      })
    } catch (error) {
      updateState({
        status: error instanceof ConsoleRequestError && error.kind === "denied"
          ? "denied"
          : "retry",
      })
    }
  }, [])

  useEffect(() => { void load() }, [load])

  useEffect(() => {
    if (jobId === null) return
    let cancelled = false
    let timer: number | undefined
    const poll = async () => {
      try {
        const next = await readDispatchOptimizationJob(jobId)
        if (cancelled) return
        updateState({ job: next })
        if (["complete", "failed", "conflict", "denied"].includes(next.status)) {
          updateState({
            jobId: null,
            notice: {
              tone: next.status === "complete" && next.proposal?.state === "Feasible"
                ? "success"
                : next.status === "complete"
                  ? "warning"
                  : "danger",
              message: next.message,
            },
          })
          await load()
          return
        }
        timer = window.setTimeout(() => { void poll() }, 500)
      } catch {
        if (!cancelled) {
          updateState({
            jobId: null,
            notice: {
              tone: "danger",
              message: "Planning status could not be confirmed. The draft was not shown as changed.",
            },
          })
        }
      }
    }
    void poll()
    return () => {
      cancelled = true
      if (timer !== undefined) window.clearTimeout(timer)
    }
  }, [jobId, load])

  useEffect(() => {
    if (!publishOpen) return
    if (publishDialog.current !== null && !publishDialog.current.open) {
      publishDialog.current.showModal()
    }
    confirmButton.current?.focus()
  }, [publishOpen, busy])

  useEffect(() => {
    if (dayOfSelection === null) return
    if (dayOfDialog.current !== null && !dayOfDialog.current.open) {
      dayOfDialog.current.showModal()
    }
    dayOfConfirmButton.current?.focus()
  }, [dayOfSelection, busy])

  const action = async (
    input: Parameters<typeof dispatchBoardAction>[0],
    success: string
  ) => {
    updateState({ busy: input.action, notice: null })
    try {
      const result = await dispatchBoardAction(input)
      if (result.status === "confirmed") {
        updateState({ notice: { tone: "success", message: success } })
      } else if (result.status === "conflict") {
        updateState({
          notice: {
            tone: "warning",
            message: "This Dispatch Run changed in another session. The current version has been refreshed.",
          },
        })
      } else if (result.status === "denied") {
        updateState({
          notice: {
            tone: "danger",
            message: "Your current Membership no longer allows this action.",
          },
        })
      } else {
        updateState({
          notice: {
            tone: "danger",
            message: "Frappe did not confirm the action. Nothing is shown as complete.",
          },
        })
      }
      await load()
      return result.status === "confirmed"
    } catch {
      updateState({
        notice: {
          tone: "danger",
          message: "Frappe did not confirm the action. Nothing is shown as complete.",
        },
      })
      return false
    } finally {
      updateState({ busy: null })
    }
  }

  const setPin = (
    stopId: DispatchBoardReadV1["routes"][number]["stops"][number]["stopId"],
    pinned: boolean
  ) => {
    if (board === null) return
    const pins = new Set(board.planning.pinnedStopIds)
    if (pinned) pins.add(stopId)
    else pins.delete(stopId)
    void action({
      dispatchRunId: board.dispatchRunId,
      expectedVersion: board.version,
      action: "constraints",
      reason,
      pinnedStopIds: [...pins],
      lockedRouteIds: board.planning.lockedRouteIds,
    }, pinned ? "Visit pinned to its route position." : "Visit pin released.")
  }

  const setLock = (
    routeId: DispatchBoardReadV1["routes"][number]["routeId"],
    locked: boolean
  ) => {
    if (board === null) return
    const locks = new Set(board.planning.lockedRouteIds)
    if (locked) locks.add(routeId)
    else locks.delete(routeId)
    void action({
      dispatchRunId: board.dispatchRunId,
      expectedVersion: board.version,
      action: "constraints",
      reason,
      pinnedStopIds: board.planning.pinnedStopIds,
      lockedRouteIds: [...locks],
    }, locked ? "Route locked against optimization." : "Route lock released.")
  }

  const moveStop = (routeIndex: number, stopIndex: number, direction: -1 | 1) => {
    const nextRoutes = routes.map((route, index) => {
      if (index !== routeIndex) return route
      const target = stopIndex + direction
      if (target < 0 || target >= route.stops.length) return route
      const stops = [...route.stops]
      const selected = stops[stopIndex]
      const adjacent = stops[target]
      if (selected === undefined || adjacent === undefined) return route
      stops[stopIndex] = adjacent
      stops[target] = selected
      return {
        ...route,
        stops: stops.map((stop, position) => ({ ...stop, sequence: position + 1 })),
      }
    })
    updateState({ routes: nextRoutes, dirty: true })
  }

  const saveOrder = () => {
    if (board === null) return
    void action({
      dispatchRunId: board.dispatchRunId,
      expectedVersion: board.version,
      action: "revise",
      routes,
      reason,
    }, board.state === "Published"
      ? "A new working draft was opened without changing the published route."
      : "Manual route order saved with its reason.")
  }

  const optimize = async () => {
    if (board === null) return
    updateState({ busy: "optimize", notice: null })
    try {
      const started = await startDispatchOptimization({
        dispatchRunId: board.dispatchRunId,
        expectedVersion: board.version,
        reason,
      })
      updateState({
        job: {
          responseVersion: 1,
          jobId: started.jobId,
          status: "queued",
          stage: "queued",
          progressPercent: 0,
          message: "Route planning is queued.",
          proposal: null,
          errorCode: null,
        },
        jobId: started.jobId,
      })
    } catch {
      updateState({
        notice: {
          tone: "danger",
          message: "Route planning could not start. The draft was not changed.",
        },
      })
    } finally {
      updateState({ busy: null })
    }
  }

  const applyProposal = () => {
    if (board?.proposal === null || board?.proposal === undefined) return
    void action({
      dispatchRunId: board.dispatchRunId,
      expectedVersion: board.version,
      action: "apply",
      proposalId: board.proposal.entityId,
      reason,
    }, "The feasible proposal was applied to the working draft.")
  }

  const publish = async () => {
    if (board === null) return
    const confirmed = await action({
      dispatchRunId: board.dispatchRunId,
      expectedVersion: board.version,
      action: "publish",
      reason,
    }, "Dispatch published. A new immutable version is now authoritative.")
    if (confirmed) {
      updateState({ publishOpen: false })
      window.setTimeout(() => publishButton.current?.focus(), 0)
    }
  }

  const openDayOf = (
    routeId: DispatchBoardRouteV1["routeId"],
    stop: DispatchBoardStopV1,
    trigger: HTMLButtonElement
  ) => {
    dayOfReturnFocus.current = trigger
    updateState({
      dayOfSelection: { routeId, stop },
      dayOfAction: "arrival_window",
      dayOfMinutes: 15,
      notice: null,
    })
  }

  const closeDayOf = () => {
    updateState({ dayOfSelection: null })
    window.setTimeout(() => dayOfReturnFocus.current?.focus(), 0)
  }

  const applyDayOf = async () => {
    if (board === null || dayOfSelection === null) return
    const { stop } = dayOfSelection
    const shift = dayOfMinutes * 60_000
    updateState({ busy: "day-of", notice: null })
    try {
      const result = await dispatchDayOfChange({
        dispatchRunId: board.dispatchRunId,
        expectedRunVersion: board.version,
        basePublishedVersion: board.latestPublishedVersion,
        reason,
        change: dayOfAction === "cancel"
          ? {
              changeType: "cancel",
              visitId: stop.visitId,
              expectedVisitVersion: stop.visitVersion,
              cancellationCode: "operational",
            }
          : {
              changeType: "arrival_window",
              visitId: stop.visitId,
              expectedVisitVersion: stop.visitVersion,
              startsAt: new Date(Date.parse(stop.startsAt) + shift).toISOString(),
              endsAt: new Date(Date.parse(stop.endsAt) + shift).toISOString(),
              timezone: stop.timezone,
            },
      })
      if (result.status === "confirmed") {
        updateState({
          notice: {
            tone: "success",
            message: `Day-of change published as immutable version ${result.currentPublishedVersion}.`,
          },
        })
        closeDayOf()
      } else if (result.status === "conflict") {
        updateState({
          dayOfSelection: null,
          notice: {
            tone: "warning",
            message: `This run changed in another session. Record v${result.currentRunVersion ?? "?"}, published v${result.currentPublishedVersion ?? "?"} is now loaded; review and reapply.`,
          },
        })
      } else if (result.status === "denied") {
        updateState({
          dayOfSelection: null,
          notice: {
            tone: "danger",
            message: "Your current Membership no longer allows day-of publication changes.",
          },
        })
      } else {
        updateState({
          notice: {
            tone: "danger",
            message: "Frappe did not confirm the change. The current publication remains authoritative.",
          },
        })
      }
      await load()
    } catch {
      updateState({
        notice: {
          tone: "danger",
          message: "Frappe did not confirm the change. The current publication remains authoritative.",
        },
      })
    } finally {
      updateState({ busy: null })
    }
  }

  const routeById = useMemo(
    () => new Map(board?.routes.map((route) => [route.routeId, route]) ?? []),
    [board]
  )
  const publishingReady = board?.state === "Draft"
    && board.planning.feasibility === "Feasible"
    && board.permissions.canPublish
    && !dirty

  return {
    status,
    board,
    map,
    routes,
    dirty,
    reason,
    busy,
    notice,
    jobId,
    job,
    publishOpen,
    dayOfSelection,
    dayOfAction,
    dayOfMinutes,
    publishButton,
    confirmButton,
    publishDialog,
    dayOfDialog,
    dayOfConfirmButton,
    routeById,
    publishingReady,
    updateState,
    load,
    setPin,
    setLock,
    moveStop,
    saveOrder,
    optimize,
    applyProposal,
    publish,
    openDayOf,
    closeDayOf,
    applyDayOf,
  }
}

export const DispatchBoard = () => {
  const {
    status,
    board,
    map,
    routes,
    dirty,
    reason,
    busy,
    notice,
    jobId,
    job,
    publishOpen,
    dayOfSelection,
    dayOfAction,
    dayOfMinutes,
    publishButton,
    confirmButton,
    publishDialog,
    dayOfDialog,
    dayOfConfirmButton,
    routeById,
    publishingReady,
    updateState,
    load,
    setPin,
    setLock,
    moveStop,
    saveOrder,
    optimize,
    applyProposal,
    publish,
    openDayOf,
    closeDayOf,
    applyDayOf,
  } = useDispatchBoardController()

  if (status === "loading") {
    return (
      <section className="dispatch-shell dispatch-loading" id="dispatch" aria-busy="true">
        <div><span className="workspace-skeleton wide" /><span className="workspace-skeleton" /></div>
        <div className="dispatch-loading-board"><span /><span /><span /></div>
      </section>
    )
  }
  if (status !== "ready" || board === null) {
    return (
      <section className="dispatch-shell dispatch-unavailable" id="dispatch" role="alert">
        <span className="dispatch-unavailable-mark" aria-hidden="true">
          {status === "denied" ? "x" : "!"}
        </span>
        <div>
          <p className="eyebrow">Dispatch board</p>
          <h2>{status === "denied" ? "Access unavailable" : "Could not confirm the current run"}</h2>
          <p>
            {status === "denied"
              ? "The current Membership cannot read this Branch's Dispatch Run."
              : "No planning state has been assumed or changed."}
          </p>
          {status === "retry" && (
            <button type="button" className="dispatch-button secondary" onClick={() => { void load() }}>
              Retry
            </button>
          )}
        </div>
      </section>
    )
  }

  return (
    <section className="dispatch-shell" id="dispatch" aria-labelledby="dispatch-title">
      <header className="dispatch-header">
        <div>
          <p className="eyebrow">Branch operations / Dispatch</p>
          <h2 id="dispatch-title">{dateLabel(board.operatingDate)}</h2>
          <div className="dispatch-subtitle">
            <span className={`dispatch-state ${board.state.toLowerCase()}`}>{board.state}</span>
            <span>Draft {board.draftRevision}</span>
            <span>Record v{board.version}</span>
            {board.latestPublishedVersion > 0 && (
              <span>Published v{board.latestPublishedVersion}</span>
            )}
          </div>
        </div>
        <div className="dispatch-header-actions">
          <button
            type="button"
            className="dispatch-button secondary"
            disabled={busy !== null || jobId !== null}
            onClick={() => { void load() }}
          >
            Refresh
          </button>
          {board.state === "Published" ? (
            <button
              type="button"
              className="dispatch-button primary"
              disabled={busy !== null}
              onClick={saveOrder}
            >
              Open working draft
            </button>
          ) : (
            <button
              type="button"
              className="dispatch-button primary"
              disabled={busy !== null || jobId !== null || dirty}
              onClick={() => { void optimize() }}
            >
              {jobId !== null ? "Planning routes..." : "Optimize routes"}
            </button>
          )}
        </div>
      </header>

      {notice !== null && (
        <div className={`dispatch-notice ${notice.tone}`} role="status">
          <span aria-hidden="true">{notice.tone === "success" ? "OK" : "!"}</span>
          <p>{notice.message}</p>
        </div>
      )}
      {job !== null && jobId !== null && <Progress job={job} />}

      <div className="dispatch-command-bar">
        <label>
          <span>Planning reason</span>
          <input
            value={reason}
            maxLength={500}
            onChange={(event) => updateState({ reason: event.target.value })}
            aria-describedby="dispatch-reason-help"
          />
        </label>
        <p id="dispatch-reason-help">
          Stored with manual changes, constraints, proposals, and publication.
        </p>
        <div className="dispatch-readiness">
          <span className={`dispatch-feasibility ${board.planning.feasibility.toLowerCase()}`}>
            <i aria-hidden="true" />
            {board.planning.feasibility}
          </span>
          <span>{board.diff.movedStopCount} changes from published</span>
        </div>
      </div>

      <div className="dispatch-map-and-queues">
        <RouteMap board={board} configuration={map} />
        <aside className="dispatch-queues" aria-label="Dispatch queues">
          <div>
            <span className="dispatch-queue-count">{board.unassigned.length}</span>
            <div><strong>Unassigned</strong><p>Visits needing a Worker or Crew</p></div>
          </div>
          <div>
            <span className="dispatch-queue-count danger">{board.exceptions.length}</span>
            <div><strong>Exceptions</strong><p>Assigned work outside this run</p></div>
          </div>
          {board.unassigned.length === 0 && board.exceptions.length === 0 ? (
            <p className="dispatch-queue-clear"><span aria-hidden="true">OK</span> No queue exceptions</p>
          ) : (
            <ul>
              {[...board.unassigned, ...board.exceptions].map((item) => (
                <li key={item.visitId}>
                  <strong>{item.label}</strong>
                  <span>{humanize(item.reasonCodes[0] ?? "dispatch_exception")}</span>
                </li>
              ))}
            </ul>
          )}
        </aside>
      </div>

      <div className="dispatch-section-heading">
        <div>
          <p className="eyebrow">Resource lanes</p>
          <h3>{board.routes.length} active {board.routes.length === 1 ? "route" : "routes"}</h3>
        </div>
        {dirty && (
          <div className="dispatch-dirty-actions" role="status">
            <span>Order changed locally</span>
            <button
              type="button"
              className="dispatch-button ghost"
              onClick={() => {
                updateState({ routes: draftRoutes(board.routes), dirty: false })
              }}
            >
              Reset
            </button>
            <button
              type="button"
              className="dispatch-button primary"
              disabled={reason.trim().length < 2 || busy !== null}
              onClick={saveOrder}
            >
              Save manual order
            </button>
          </div>
        )}
      </div>

      <div className="dispatch-lanes">
        {routes.map((route, routeIndex) => {
          const detail = routeById.get(route.routeId)
          if (detail === undefined) return null
          return (
            <DispatchLane
              key={route.routeId}
              detail={detail}
              route={route}
              routeIndex={routeIndex}
              boardState={board.state}
              canDayOf={board.permissions.canPublish}
              busy={busy !== null}
              onLock={setLock}
              onMove={moveStop}
              onPin={setPin}
              onDayOf={openDayOf}
            />
          )
        })}
      </div>

      <ProposalPanel board={board} busy={busy !== null} onApply={applyProposal} />

      <footer className="dispatch-publish-bar">
        <div>
          <p className="eyebrow">Publish review</p>
          <strong>
            {publishingReady
              ? "Feasible draft ready to publish"
              : board.state === "Published"
                ? `Immutable version ${board.latestPublishedVersion} is authoritative`
                : "Resolve feasibility before publishing"}
          </strong>
          <span>
            {board.routes.length} routes / {board.routes.reduce((sum, route) => sum + route.stops.length, 0)} Visits
          </span>
        </div>
        <button
          ref={publishButton}
          type="button"
          className="dispatch-button publish"
          disabled={!publishingReady || busy !== null}
          onClick={() => updateState({ publishOpen: true })}
        >
          Review and publish
        </button>
      </footer>

      <PublishDialog
        open={publishOpen}
        dialogRef={publishDialog}
        confirmRef={confirmButton}
        board={board}
        reason={reason}
        busy={busy}
        onCancel={() => {
          updateState({ publishOpen: false })
          window.setTimeout(() => publishButton.current?.focus(), 0)
        }}
        onPublish={() => { void publish() }}
      />
      <DayOfDialog
        open={dayOfSelection !== null}
        dialogRef={dayOfDialog}
        confirmRef={dayOfConfirmButton}
        selection={dayOfSelection}
        action={dayOfAction}
        minutes={dayOfMinutes}
        reason={reason}
        busy={busy}
        onAction={(value) => updateState({ dayOfAction: value })}
        onMinutes={(value) => updateState({ dayOfMinutes: value })}
        onCancel={closeDayOf}
        onConfirm={() => { void applyDayOf() }}
      />
    </section>
  )
}
